import clsx from "clsx";
import React, { Fragment } from "react";
import { Controller, type FieldErrors, type SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { FormattedMessage, type MessageDescriptor, useIntl } from "react-intl";
import Select from "react-select";

import { featureFlags } from "~/common/feature-flags";
import { formatTime } from "~/common/formatters";
import { ErrorMessage } from "~/components/ui/error-message";
import { Label } from "~/components/ui/label";
import { LabelledInput } from "~/components/ui/labelled-input";
import { LabelledTextArea } from "~/components/ui/labelled-textarea";
import { LoadingButton } from "~/components/ui/loading-button";
import type { DateTimeStringUTC } from "~/types/common";

import { formatDate, getTimeZone } from "./calendar-utils";
import type { TExpert, TMeeting, TMeetingPatientOption } from "./meetings-types";
import { useIsRedesignMeetings } from "./redesign-meetings-context";

export type BookMeetingInputs = {
  name: string;
  phone: string;
  email: string;
  patients: {
    id: string;
    theme: string;
    question: string;
  }[];
};

const MAX_PATIENTS = 3;

const THEME_OPTIONS = [{ value: "Обсуждение 3D - плана" }, { value: "Обсуждение отсутствия перемещений зубов" }, { value: "Обсуждение лечения пациента" }, { value: "Заполнение рецепта " }];

/** Тема «Заполнение рецепта» доступна только у Лисавиной Луизы Артуровны */
const PRESCRIPTION_THEME_VALUE = "Заполнение рецепта ";

function isExpertLuiza(expert: TExpert): boolean {
  return expert.last_name === "Лисавина" && expert.first_name === "Луиза";
}

function getThemeOptions(expert: TExpert | undefined) {
  if (!expert) return THEME_OPTIONS;
  return isExpertLuiza(expert) ? THEME_OPTIONS : THEME_OPTIONS.filter((o) => o.value !== PRESCRIPTION_THEME_VALUE);
}

type TStringOption = { value: string; label: string };

/**
 * Single-value `react-select` contract matching the payments screen. The local prop type avoids `any`
 * where react-select v4 cannot infer these generics.
 */
const StringSelect = Select as unknown as React.ComponentType<{
  inputId?: string;
  classNamePrefix?: string;
  isSearchable?: boolean;
  isClearable?: boolean;
  options: TStringOption[];
  value: TStringOption | null;
  onChange(value: TStringOption | null): void;
  placeholder?: React.ReactNode;
  noOptionsMessage?(): React.ReactNode;
  "aria-labelledby"?: string;
  "aria-errormessage"?: string;
}>;

/** Same `classNamePrefix` as the payments page's `ServiceSelect` — reuses its global CSS verbatim
    (redesign-accent-recolor.css), so this dropdown looks identical without duplicating any styling. */
const BOOK_FORM_SELECT_CLASS_NAME_PREFIX = "rd-service-select";

function LabelledStringSelect({
  id,
  label,
  labelProps,
  required,
  error,
  testId,
  ...selectProps
}: {
  id: string;
  label: React.ReactNode;
  labelProps?: Pick<React.ComponentProps<typeof Label>, "semibold">;
  required?: boolean;
  error?: React.ReactChild;
  testId?: string;
} & Omit<React.ComponentProps<typeof StringSelect>, "inputId" | "classNamePrefix" | "aria-errormessage">) {
  return (
    <div className="form-group">
      <Label {...labelProps} htmlFor={id} required={required}>
        {label}
      </Label>

      <div data-testid={testId}>
        <StringSelect {...selectProps} inputId={id} classNamePrefix={BOOK_FORM_SELECT_CLASS_NAME_PREFIX} aria-errormessage={error ? `${id}-error` : undefined} />
      </div>

      {error ? <ErrorMessage id={id}>{error}</ErrorMessage> : null}
    </div>
  );
}

export function BookForm({
  expert,
  time,
  patients,
  defaultValues,
  onBookMeeting,
  onBookMeetingSuccess,
  onBookMeetingError,
}: {
  expert?: TExpert;
  time: DateTimeStringUTC;
  patients: TMeetingPatientOption[];
  defaultValues: Omit<BookMeetingInputs, "patients">;
  onBookMeeting(data: BookMeetingInputs): Promise<TMeeting>;
  onBookMeetingSuccess(meeting: TMeeting): void;
  onBookMeetingError(error: unknown): void;
}) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<BookMeetingInputs>({
    defaultValues: { ...defaultValues, patients: [{ id: "", theme: "", question: "" }] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "patients" });
  const redesign = useIsRedesignMeetings();

  const onSubmit: SubmitHandler<BookMeetingInputs> = async (data) => {
    try {
      const meeting = await onBookMeeting({ ...data, phone: phoneForBooking(data.phone) });
      onBookMeetingSuccess(meeting);
    } catch (err) {
      onBookMeetingError(err);
    }
  };

  const selectedPatients = watch("patients");

  return (
    <div>
      <DateTimeHeader time={time} showTimeZone />

      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div data-testid="meeting-book-form" className="tw-flex tw-w-full tw-flex-col tw-px-[30px] tw-py-5">
          <LabelledInput
            id="name"
            data-testid="meeting-book-name-input"
            className="tw-py-2"
            label={<FormattedMessage id="meetings.form.name" />}
            labelProps={{ semibold: true }}
            required
            rounded={redesign}
            error={getErrorMessage(errors, "name")}
            {...register("name", {
              required: { value: true, message: "meetings.form.name.required" },
            })}
          />

          <Controller
            name="phone"
            control={control}
            rules={{
              required: { value: true, message: "meetings.form.phone.required" },
              pattern: { value: PHONE_PATTERN, message: "meetings.form.phone.pattern" },
            }}
            render={({ field }) => (
              <LabelledInput
                id="phone"
                data-testid="meeting-book-phone-input"
                className="tw-py-2"
                type="tel"
                inputMode="tel"
                placeholder="+7, +375, +996"
                maxLength={20}
                label={<FormattedMessage id="meetings.form.phone" />}
                labelProps={{ semibold: true }}
                required
                rounded={redesign}
                error={getErrorMessage(errors, "phone")}
                value={formatPhoneNumber(field.value)}
                onChange={(event) => {
                  const next = getRawPhoneNumber(event.target.value, field.value);
                  setValue("phone", next);
                  moveCaretToEnd(event.target, formatPhoneNumber(next).length);
                }}
              />
            )}
          />

          <LabelledInput
            id="email"
            data-testid="meeting-book-email-input"
            className="tw-py-2"
            label="Email"
            labelProps={{ semibold: true }}
            required
            rounded={redesign}
            error={getErrorMessage(errors, "email")}
            {...register("email", {
              required: { value: true, message: "meetings.form.email.required" },
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "meetings.form.email.pattern",
              },
            })}
          />

          {fields.map((field, index) => (
            <Fragment key={field.id}>
              <Controller
                name={`patients.${index}.id`}
                control={control}
                rules={{ required: { value: true, message: "meetings.form.required" } }}
                render={({ field }) => {
                  const patientOptions: TStringOption[] = getAvailablePatients(patients, selectedPatients, index).map((patient) => ({
                    value: String(patient.id),
                    label: `${patient.name} (${patient.id})`,
                  }));

                  return (
                    <LabelledStringSelect
                      id={`patient-${index}`}
                      testId={`meeting-book-patient-select-${index}`}
                      label={
                        <>
                          <FormattedMessage id="meetings.form.patient" />

                          {fields.length > 1 && (
                            <span className="tw-ml-1 tw-inline-flex tw-items-center tw-gap-1">
                              {index + 1}

                              <MinusButton
                                onClick={(event) => {
                                  // Inside a <label> — stop the click from toggling the select / focusing input.
                                  event.preventDefault();
                                  event.stopPropagation();
                                  remove(index);
                                }}
                              />
                            </span>
                          )}
                        </>
                      }
                      labelProps={{ semibold: true }}
                      required
                      isSearchable
                      isClearable
                      placeholder={<FormattedMessage id="additional_aligners.patient.placeholder" />}
                      noOptionsMessage={() => <FormattedMessage id="pat_table.patients.not_found" />}
                      options={patientOptions}
                      error={getArrayErrorMessage(errors, index, "id")}
                      value={patientOptions.find((option) => option.value === field.value) ?? null}
                      onChange={(option) => field.onChange({ target: { value: option?.value ?? "" } })}
                    />
                  );
                }}
              />

              <Controller
                name={`patients.${index}.theme`}
                control={control}
                rules={{ required: { value: true, message: "meetings.form.required" } }}
                render={({ field }) => {
                  const themeOptions: TStringOption[] = getThemeOptions(expert).map((option) => ({ value: option.value, label: option.value }));

                  return (
                    <LabelledStringSelect
                      id={`theme-${index}`}
                      testId={`meeting-book-theme-select-${index}`}
                      label={<FormattedMessage id="meetings.form.theme" />}
                      labelProps={{ semibold: true }}
                      required
                      isSearchable={false}
                      placeholder={<FormattedMessage id="meetings.form.theme.placeholder" />}
                      options={themeOptions}
                      error={getArrayErrorMessage(errors, index, "theme")}
                      value={themeOptions.find((option) => option.value === field.value) ?? null}
                      onChange={(option) => field.onChange({ target: { value: option?.value ?? "" } })}
                    />
                  );
                }}
              />

              <LabelledTextArea
                id={`question-${index}`}
                data-testid={`meeting-book-question-input-${index}`}
                label={<FormattedMessage id="meetings.form.question" />}
                labelProps={{ semibold: true }}
                rows={5}
                required
                rounded={redesign}
                error={getArrayErrorMessage(errors, index, "question")}
                {...register(`patients.${index}.question`, {
                  required: { value: true, message: "meetings.form.required" },
                })}
              />
            </Fragment>
          ))}

          {fields.length < MAX_PATIENTS ? <PlusButton onClick={() => append({ id: "", theme: "", question: "" })} /> : null}
        </div>

        <div className={clsx("tw-px-[30px] tw-pb-3.5 tw-pt-[30px]", redesign ? undefined : "tw-border-t tw-border-[#f1f1f1] tw-text-center")}>
          <LoadingButton
            type="submit"
            variant="primary"
            size="xl"
            // Match redesign button corners while preserving the square legacy button.
            rounded={featureFlags.redesignV1 || undefined}
            className={redesign ? "tw-h-10 tw-w-full" : undefined}
            isLoading={isSubmitting}
            data-testid="meeting-book-submit"
          >
            <FormattedMessage id="meetings.book" />
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}

function PlusButton({ onClick }: { onClick: React.MouseEventHandler<HTMLButtonElement> }) {
  const intl = useIntl();

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "tw-size-10 tw-border-none tw-bg-brand-green tw-p-0 tw-uppercase tw-text-white",
        "tw-transition-colors tw-duration-250 hover:tw-bg-brand-green-hover",
        "tw-mx-auto tw-block tw-rounded-full tw-text-2xl",
      )}
      aria-label={intl.formatMessage({ id: "meetings.form.add" })}
    >
      +
    </button>
  );
}

function MinusButton({ onClick }: { onClick: React.MouseEventHandler<HTMLButtonElement> }) {
  const intl = useIntl();

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        // Keep the control inline with the patient index in both form layouts.
        "tw-inline-flex tw-size-6 tw-items-center tw-justify-center",
        "tw-rounded tw-border-none tw-bg-[#e7505a] tw-text-white",
        "tw-transition-colors tw-duration-250 hover:tw-bg-[#e12330]",
      )}
      aria-label={intl.formatMessage({ id: "meetings.form.remove" })}
    >
      -
    </button>
  );
}

export function DateTimeHeader({ time, showTimeZone }: { time: DateTimeStringUTC; showTimeZone?: boolean }) {
  const redesign = useIsRedesignMeetings();
  return (
    <div
      className={clsx(
        "tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-b tw-px-[26px] tw-py-5",
        redesign ? "tw-border-border tw-font-sans tw-text-foreground" : "tw-border-[#f1f1f1]",
      )}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <span>{formatTime(time)}</span>
        <span aria-hidden="true" className={redesign ? "tw-text-muted-foreground" : "tw-text-[#adadad]"}>
          •
        </span>
        <span>{formatDate(new Date(time))}</span>
      </div>

      {showTimeZone ? <span className="tw-opacity-50">{getTimeZone()}</span> : null}
    </div>
  );
}

// Russia +7 (10 digits), Belarus +375 (9), Kyrgyzstan +996 (9).
// 3/37 and 9/99 stay open so the doctor can finish 375 or 996.
// 996 is also a Russian mobile prefix: ten digits stay +996 on screen and become +7 on submit.
// A 7 or 8 typed first is Russia. Longer than ten digits after that 7 is Kyrgyzstan.
const PHONE_PATTERN = /^(\+7\d{10}|\+375\d{9}|\+996\d{7}|\+996\d{9})$/;

type Phone =
  | { country: "empty" }
  | { country: "open"; digits: string }
  | { country: "ru" | "by" | "kg"; national: string };

function onlyDigits(value: string): string {
  return value.replace(/\D/g, "");
}

function readPhone(digits: string): Phone {
  if (!digits) return { country: "empty" };
  if (digits.startsWith("375")) return { country: "by", national: digits.slice(3, 12) };
  if (digits === "3" || digits === "37" || digits === "9" || digits === "99") return { country: "open", digits };

  if (digits.startsWith("7") || digits.startsWith("8")) {
    const local = digits.slice(1);
    if (local.startsWith("996") && local.length > 10) return { country: "kg", national: local.slice(3, 12) };
    return { country: "ru", national: local.slice(0, 10) };
  }

  if (digits.startsWith("996")) return { country: "kg", national: digits.slice(3, 12) };
  return { country: "ru", national: digits.slice(0, 10) };
}

function formatGroups(prefix: string, national: string, groups: readonly number[]): string {
  if (!national) return prefix;

  const areaLength = groups[0];
  let text = `${prefix}(${national.slice(0, areaLength)}`;
  if (national.length >= areaLength) text += ")";

  let cursor = areaLength;
  for (let group = 1; group < groups.length && cursor < national.length; group++) {
    const part = national.slice(cursor, cursor + groups[group]);
    text += group === 1 ? part : `-${part}`;
    cursor += groups[group];
  }
  return text;
}

function formatPhone(phone: Phone): string {
  if (phone.country === "empty") return "";
  if (phone.country === "open") return `+${phone.digits}`;
  if (phone.country === "by") return formatGroups("+375", phone.national, [2, 3, 2, 2]);
  if (phone.country === "kg") return formatGroups("+996", phone.national, [3, 3, 3]);
  return formatGroups("+7", phone.national, [3, 3, 2, 2]);
}

function rawPhone(phone: Phone): string {
  if (phone.country === "empty") return "";
  if (phone.country === "open") return `+${phone.digits}`;
  if (phone.country === "by") return `+375${phone.national}`;
  if (phone.country === "kg") return `+996${phone.national}`;
  return phone.national ? `+7${phone.national}` : "+7";
}

function formatPhoneNumber(value: string): string {
  return formatPhone(readPhone(onlyDigits(value)));
}

function getRawPhoneNumber(value: string, previous = ""): string {
  const previousDigits = onlyDigits(previous);
  let digits = onlyDigits(value);
  const deletedMaskCharacter =
    previousDigits.length > 0 && digits === previousDigits && value.length < formatPhoneNumber(previous).length;
  if (deletedMaskCharacter) digits = previousDigits.slice(0, -1);
  return rawPhone(readPhone(digits));
}

function phoneForBooking(phone: string): string {
  if (/^\+996\d{7}$/.test(phone)) return `+7${phone.slice(1)}`;
  return phone;
}

function moveCaretToEnd(input: HTMLInputElement, position: number) {
  requestAnimationFrame(() => {
    if (input.isConnected) input.setSelectionRange(position, position);
  });
}

function getAvailablePatients(patients: TMeetingPatientOption[], selectedPatients: BookMeetingInputs["patients"], index: number): TMeetingPatientOption[] {
  const selectedPatientIds = selectedPatients.map((patient) => Number.parseInt(patient.id)).filter(Number.isInteger);

  const otherSelectedPatientsIds = [...selectedPatientIds.slice(0, index), ...selectedPatientIds.slice(index + 1)];

  return patients.filter((patient) => !otherSelectedPatientsIds.includes(patient.id));
}

function getErrorMessage(errors: FieldErrors<BookMeetingInputs>, field: keyof BookMeetingInputs) {
  const message = errors[field]?.message;
  return message ? <FormattedMessage id={message as MessageDescriptor["id"]} /> : undefined;
}

function getArrayErrorMessage(errors: FieldErrors<BookMeetingInputs>, index: number, field: keyof BookMeetingInputs["patients"][number]) {
  const message = errors.patients?.[index]?.[field]?.message;
  return message ? <FormattedMessage id={message as MessageDescriptor["id"]} /> : undefined;
}
